import React, { useState, useEffect } from 'react';
import { GameState } from '../domain/types';
import MagicCounter from '../components/MagicCounter';
import OptionsGrid from '../components/OptionsGrid';
import StarMeter from '../components/StarMeter';
import './GameScreen.css';

interface GameScreenProps {
  gameState: GameState;
  onAnswer: (answer: number) => void;
  onNext: () => void;
  onComplete: () => void;
  reducedMotion?: boolean;
  totalStars: number;
}

const GameScreen: React.FC<GameScreenProps> = ({
  gameState,
  onAnswer,
  onNext,
  onComplete,
  reducedMotion = false,
  totalStars
}) => {
  const [selectedAnswer, setSelectedAnswer] = useState<number | null>(null);
  const [showResult, setShowResult] = useState(false);
  const [message, setMessage] = useState('');
  const [showCelebration, setShowCelebration] = useState(false);
  const [showWrongFeedback, setShowWrongFeedback] = useState(false);

  const currentChallenge = gameState.challenges[gameState.currentChallenge];
  const isLastChallenge = gameState.currentChallenge >= gameState.challenges.length - 1;

  useEffect(() => {
    // Reset state when challenge changes
    setSelectedAnswer(null);
    setShowResult(false);
    setMessage('Cata, junta los objetos y elige el total.');
    setShowCelebration(false);
  }, [gameState.currentChallenge]);

  const handleAnswer = (answer: number) => {
    // If showing result and it's not the correct answer, allow retry
    if (showResult && answer !== currentChallenge.correct) {
      setShowResult(false);
      setMessage('Cata, junta los objetos y elige el total.');
      setShowCelebration(false);
      setShowWrongFeedback(false);
    }
    
    // Allow clicking correct answer even when showing results
    
    setSelectedAnswer(answer);
    onAnswer(answer);
    
    const isCorrect = answer === currentChallenge.correct;
    setMessage(isCorrect ? '¡Muy bien, Princesa Cata! 🌟' : 'Intenta otra vez, Cata ✨');
    setShowResult(true);
    
    if (isCorrect) {
      setShowCelebration(true);
      setShowWrongFeedback(false);
      // Hide celebration after animation
      setTimeout(() => setShowCelebration(false), 2000);
    } else {
      setShowWrongFeedback(true);
      // Hide wrong feedback after animation
      setTimeout(() => setShowWrongFeedback(false), 2000);
    }

    // Auto-advance only if correct or after 2 errors
    if (isCorrect) {
      setTimeout(() => {
        if (isLastChallenge) {
          onComplete();
        } else {
          onNext();
        }
      }, 2000);
    } else if (gameState.errors >= 2) {
      // After 2 errors, auto-advance after showing hint
      setTimeout(() => {
        if (isLastChallenge) {
          onComplete();
        } else {
          onNext();
        }
      }, 3000);
    }
  };

  const handleNext = () => {
    if (isLastChallenge) {
      onComplete();
    } else {
      onNext();
    }
  };

  return (
    <div className="game-screen">
      {/* Celebration particles */}
      {showCelebration && (
        <div className="celebration-particles">
          <img 
            src="/assets/Celebration.png" 
            alt="Cata celebrando" 
            className="celebration-main-image"
          />
          {Array.from({ length: 12 }, (_, i) => (
            <div
              key={i}
              className="celebration-particle"
              style={{
                left: `${Math.random() * 100}%`,
                top: `${Math.random() * 100}%`,
                animationDelay: `${Math.random() * 0.5}s`,
                animationDuration: `${1 + Math.random() * 0.5}s`
              }}
            >
              {['✨', '🌟', '⭐', '💫'][Math.floor(Math.random() * 4)]}
            </div>
          ))}
        </div>
      )}

      {/* Wrong feedback particles */}
      {showWrongFeedback && (
        <div className="wrong-particles">
          <img 
            src="/assets/Wrong.png" 
            alt="Cata triste" 
            className="wrong-main-image"
          />
          {Array.from({ length: 8 }, (_, i) => (
            <div
              key={i}
              className="wrong-particle"
              style={{
                left: `${Math.random() * 100}%`,
                top: `${Math.random() * 100}%`,
                animationDelay: `${Math.random() * 0.5}s`,
                animationDuration: `${1 + Math.random() * 0.5}s`
              }}
            >
              {['😢', '💔', '❌', '😞'][Math.floor(Math.random() * 4)]}
            </div>
          ))}
        </div>
      )}
      
      <div className="game-container">
        {/* Header con progreso */}
        <div className="game-header">
          <div className="challenge-progress">
            Reto {gameState.currentChallenge + 1} de {gameState.challenges.length}
          </div>
          <StarMeter 
            current={totalStars} 
            total={5}
          />
        </div>

        {/* Mensaje del reto */}
        <div className="challenge-message">
          {message}
        </div>

        {/* Contador mágico */}
        <MagicCounter
          challenge={currentChallenge}
          showHint={gameState.showHint}
          reducedMotion={reducedMotion}
        />

        {/* Opciones de respuesta */}
        <OptionsGrid
          options={currentChallenge.options}
          correctAnswer={currentChallenge.correct}
          onSelect={handleAnswer}
          selectedAnswer={selectedAnswer}
          showResult={showResult}
          reducedMotion={reducedMotion}
        />

        {/* Botón de siguiente (solo si hay resultado y es correcto) */}
        {showResult && selectedAnswer === currentChallenge.correct && (
          <button 
            className="next-button"
            onClick={handleNext}
          >
            {isLastChallenge ? 'Ver corona mágica 👑' : 'Siguiente reto ✨'}
          </button>
        )}

        {/* Ayuda visual si hay 2 errores */}
        {gameState.showHint && (
          <div className="hint-strip">
            <div className="hint-text">
              Contemos juntas: 1… 2… 3… ¡Ahora elige el total!
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default GameScreen;
